'use client';

import { useDateParts } from '@shared/hooks';
import { Ellipse } from '@shared/icons';
import { twMerge } from '@shared/themes';
import { useFormatter, useTranslations } from 'next-intl';
import type { ComponentProps } from 'react';
import type { ParticipationState } from '../../lottery-purchases/types';
import {
  type DrawSelectorState,
  DrawSelectorButton,
  DrawSelectorDateContainer,
  DrawSelectorIcon,
  DrawSelectorText,
} from './components/DrawSelectorItemPrimitives/DrawSelectorItemPrimitives';

type DrawSelectorVariant = 'date' | 'time' | 'dateTime' | 'highFrequency';

type BaseDrawSelectorItemProps = Omit<ComponentProps<'div'>, 'children'> & {
  state?: DrawSelectorState;
  onClick?: () => void;
  'data-testid'?: string;
};

type DrawSelectorItemProps =
  | (BaseDrawSelectorItemProps & {
      variant?: DrawSelectorVariant;
      date: Date;
      drawingId: string;
      participationState: ParticipationState;
      olderDraws?: false;
    })
  | (BaseDrawSelectorItemProps & {
      variant: 'olderDraws';
      date?: never;
      participationState?: never;
      olderDraws: true;
    });

type BaseDrawSelectorVariantProps = ComponentProps<typeof DrawSelectorButton> & {
  state: DrawSelectorState;
};

type DrawSelectorVariantProps = BaseDrawSelectorVariantProps & {
  date: Date;
  drawingId?: string;
};

type DrawSelectorOlderDrawsProps = BaseDrawSelectorVariantProps;

const drawSelectorVariantsMap = {
  date: DrawSelectorDate,
  time: DrawSelectorTime,
  dateTime: DrawSelectorDateTime,
  highFrequency: DrawSelectorHighFrequency,
  olderDraws: DrawSelectorOlderDraws,
} as const;

const minWidthByVariant = {
  date: 'min-w-14',
  time: 'min-w-16',
  dateTime: 'min-w-34',
  highFrequency: 'min-w-14',
  olderDraws: 'min-w-14',
} as const;

function DrawSelectorDate({ state, date, ...props }: DrawSelectorVariantProps) {
  const { day, month, weekday } = useDateParts(date);
  return (
    <>
      <DrawSelectorButton state={state} className="min-w-14 h-18 flex-col" {...props}>
        <DrawSelectorDateContainer className="flex-col">
          <DrawSelectorText>{day}</DrawSelectorText>
          <DrawSelectorText size="small">{month}</DrawSelectorText>
        </DrawSelectorDateContainer>
      </DrawSelectorButton>
      <DrawSelectorText size="small">{weekday}</DrawSelectorText>
    </>
  );
}

function DrawSelectorTime({ state, date, ...props }: DrawSelectorVariantProps) {
  const { time } = useDateParts(date);
  return (
    <DrawSelectorButton state={state} className="min-w-16 h-14" {...props}>
      <DrawSelectorText>{time}</DrawSelectorText>
    </DrawSelectorButton>
  );
}

function DrawSelectorDateTime({ state, date, ...props }: DrawSelectorVariantProps) {
  const { day, month, time, weekday } = useDateParts(date);
  return (
    <>
      <DrawSelectorButton state={state} className="min-w-34 h-14" {...props}>
        <DrawSelectorDateContainer>
          <DrawSelectorText>{day}</DrawSelectorText>
          <DrawSelectorText size="small">{month}</DrawSelectorText>
        </DrawSelectorDateContainer>
        <DrawSelectorText>{time}</DrawSelectorText>
      </DrawSelectorButton>
      <DrawSelectorText size="small">{weekday}</DrawSelectorText>
    </>
  );
}

function DrawSelectorHighFrequency({ state, date, drawingId = '', ...props }: DrawSelectorVariantProps) {
  const { day, month, time } = useDateParts(date);
  return (
    <>
      <DrawSelectorButton state={state} className="min-w-14 h-18 flex-col" {...props}>
        <DrawSelectorDateContainer className="flex-col">
          <DrawSelectorText>{day}</DrawSelectorText>
          <DrawSelectorText size="small">{month}</DrawSelectorText>
        </DrawSelectorDateContainer>
      </DrawSelectorButton>
      <div className="@container flex w-full justify-center">
        <DrawSelectorText size="small" className="flex flex-wrap items-center justify-center gap-x-1 text-center">
          <span className="paragraph-s">{time}</span>
          {drawingId && (
            <>
              <Ellipse size="extra-tiny" className="hidden h-[2px] w-[2px] shrink-0 @min-[6.5rem]:inline-block" />
              <span className="paragraph-s">#{drawingId}</span>
            </>
          )}
        </DrawSelectorText>
      </div>
    </>
  );
}

function DrawSelectorOlderDraws({ state, ...props }: DrawSelectorOlderDrawsProps) {
  const t = useTranslations('lotteries.purchase');
  return (
    <>
      <DrawSelectorButton state={state} className="min-w-14 h-14" {...props}>
        <DrawSelectorText size="small">
          {t('older_participations_btn')
            .split('\n')
            .map((line, index) => (
              <span key={index}>
                {line}
                {index === 0 && <br />}
              </span>
            ))}
        </DrawSelectorText>
      </DrawSelectorButton>
      <DrawSelectorText size="small">&nbsp;</DrawSelectorText>
    </>
  );
}

function DrawSelectorItem(props: DrawSelectorItemProps) {
  const { className, state = 'default', variant = 'date', onClick, olderDraws } = props;
  const t = useTranslations('lotteries.purchase');
  const DrawSelectorVariant = drawSelectorVariantsMap[variant];
  const classes = {
    container: twMerge('relative flex flex-1 flex-col items-center justify-center gap-2', minWidthByVariant[variant], state === 'disabled' ? 'opacity-40' : '', className),
  };
  const format = useFormatter();

  if (olderDraws) {
    return (
      <div className={classes.container} {...props}>
        <DrawSelectorOlderDraws state={state} aria-label={t('older_participations_ariaLabel')} onClick={onClick} />
      </div>
    );
  }

  const { drawingId, date, participationState } = props;
  const shouldShowIcon = variant !== 'dateTime' && state === 'default';
  const ariaLabel = `${format.dateTime(date, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })}, ${state}`;

  return (
    <div className={classes.container} {...props}>
      <DrawSelectorVariant state={state} date={date} drawingId={drawingId} aria-label={ariaLabel} onClick={onClick} />
      {shouldShowIcon ? <DrawSelectorIcon participationState={participationState} /> : null}
    </div>
  );
}

export { DrawSelectorItem };
export type { DrawSelectorItemProps, DrawSelectorState, DrawSelectorVariant };
